import React, { useEffect, useMemo, useState } from "react";
import BlogEditor from "./BlogEditor";
import { slugify, todayStr } from "./blogUtils";
import { showToast } from "../../Pages/Utils/utils";
import "./blog-editor.css";

const PUBLIC_BASE = "https://stride-capital.com/blog/";

const BlogForm = ({ initialData = {}, onSubmit, loading, buttonName = "Create" }) => {
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [slugTouched, setSlugTouched] = useState(false);
    const [kicker, setKicker] = useState("");
    const [description, setDescription] = useState("");
    const [author, setAuthor] = useState("Stride Capital");
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [coverFile, setCoverFile] = useState(null);
    const [coverUrl, setCoverUrl] = useState(null);
    const [removeCover, setRemoveCover] = useState(false);
    const [status, setStatus] = useState("draft");
    const [publishedAt, setPublishedAt] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [bodyEmpty, setBodyEmpty] = useState(true);
    const [errors, setErrors] = useState({});

    const isEdit = !!(initialData && initialData.id);

    useEffect(() => {
        if (!initialData || Object.keys(initialData).length === 0) return;
        setTitle(initialData.title || "");
        setSlug(initialData.slug || "");
        setSlugTouched(!!initialData.slug);
        setKicker(initialData.kicker || "");
        setDescription(initialData.description || "");
        setAuthor(initialData.author || "Stride Capital");
        let t = initialData.tags;
        if (typeof t === "string") {
            try { t = JSON.parse(t); } catch (e) { t = []; }
        }
        setTags(Array.isArray(t) ? t : []);
        setCoverUrl(initialData.cover_image_url || null);
        setStatus(initialData.status || "draft");
        setPublishedAt(initialData.published_at ? String(initialData.published_at).slice(0, 10) : "");
        setBodyHtml(initialData.body_html || "");
        setBodyEmpty(!initialData.body_html);
    }, [initialData]);

    const coverPreview = useMemo(
        () => (coverFile ? URL.createObjectURL(coverFile) : null),
        [coverFile]
    );

    const onTitleChange = (v) => {
        setTitle(v);
        if (!slugTouched) setSlug(slugify(v));
    };

    const addTag = (raw) => {
        const t = raw.trim();
        if (!t) return;
        if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
        setTags([...tags, t]);
    };

    const onTagKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addTag(tagInput);
            setTagInput("");
        } else if (e.key === "Backspace" && !tagInput && tags.length) {
            setTags(tags.slice(0, -1));
        }
    };

    const onCoverPick = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!f) return;
        setCoverFile(f);
        setRemoveCover(false);
    };

    const onCoverRemove = () => {
        setCoverFile(null);
        if (coverUrl) setRemoveCover(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errs = {};
        if (!title.trim()) errs.title = "Title is required";
        if (bodyEmpty || !bodyHtml.trim()) errs.body = "Body is required";
        setErrors(errs);
        if (Object.keys(errs).length) {
            showToast("Please fix the highlighted fields", "error");
            return;
        }
        const finalTags = tagInput.trim()
            ? [...tags, tagInput.trim()].filter((v, i, a) => a.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i)
            : tags;
        onSubmit({
            title: title.trim(),
            slug: slug.trim(),
            kicker: kicker.trim(),
            description: description.trim(),
            body_html: bodyHtml,
            author: author.trim(),
            tags: finalTags,
            status,
            published_at: publishedAt || (status === "published" ? todayStr() : ""),
            file: coverFile,
            remove_cover_image: removeCover && !coverFile,
        });
    };

    const shownCover = coverPreview || (!removeCover ? coverUrl : null);

    return (
        <form className="card mb-4" onSubmit={handleSubmit}>
            <div className="card-body">
                <div className="form-group">
                    <label>Title *</label>
                    <input
                        className={`form-control ${errors.title ? "is-invalid" : ""}`}
                        value={title}
                        onChange={(e) => onTitleChange(e.target.value)}
                    />
                    {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                </div>

                <div className="form-group mt-3">
                    <label>Slug</label>
                    <input
                        className="form-control"
                        value={slug}
                        onChange={(e) => {
                            setSlugTouched(true);
                            setSlug(slugify(e.target.value));
                        }}
                        placeholder="auto-generated from the title"
                    />
                    <small className="text-muted d-block">
                        Public URL: {PUBLIC_BASE}{slug || "<slug>"}
                    </small>
                    <small className="text-warning d-block">
                        Changing the slug of a published post breaks its existing URL.
                    </small>
                </div>

                <div className="form-group mt-3">
                    <label>Kicker</label>
                    <input
                        className="form-control"
                        value={kicker}
                        onChange={(e) => setKicker(e.target.value)}
                    />
                    <small className="text-muted">
                        Shown above the title, e.g. the question investors ask.
                    </small>
                </div>

                <div className="form-group mt-3">
                    <label>Description (meta description)</label>
                    <textarea
                        className="form-control"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                    <small className={description.length > 155 ? "text-danger" : "text-muted"}>
                        {description.length} characters
                        {description.length > 155 ? " - over 155, search engines may truncate it" : " (aim for 155 or fewer)"}
                    </small>
                </div>

                <div className="form-group mt-3">
                    <label>Author</label>
                    <input
                        className="form-control"
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                    />
                </div>

                <div className="form-group mt-3">
                    <label>Tags</label>
                    <div className="mb-1">
                        {tags.map((t, i) => (
                            <span key={t} className={`blog-tag-chip ${i === 0 ? "first" : ""}`}>
                                {t}
                                <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} aria-label={`Remove ${t}`}>
                                    &times;
                                </button>
                            </span>
                        ))}
                    </div>
                    <input
                        className="form-control"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={onTagKeyDown}
                        onBlur={() => { addTag(tagInput); setTagInput(""); }}
                        placeholder="Type a tag and press Enter"
                    />
                    <small className="text-muted">The first tag is shown as the card label.</small>
                </div>

                <div className="form-group mt-3">
                    <label>Cover image (optional)</label>
                    <div className="d-flex align-items-center" style={{ gap: 16 }}>
                        <label className="btn btn-outline-primary mb-0">
                            {shownCover ? "Replace image" : "Select image"}
                            <input hidden type="file" accept="image/*" onChange={onCoverPick} />
                        </label>
                        {shownCover && (
                            <>
                                <img
                                    src={shownCover}
                                    alt="cover"
                                    style={{ width: 160, height: 90, objectFit: "cover", borderRadius: 8 }}
                                />
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={onCoverRemove}>
                                    Remove
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-md-6 form-group">
                        <label>Status</label>
                        <div>
                            <label className="mr-4 mb-0">
                                <input type="radio" name="blog-status" checked={status === "draft"} onChange={() => setStatus("draft")} /> Draft
                            </label>
                            <label className="mb-0 ml-3">
                                <input type="radio" name="blog-status" checked={status === "published"} onChange={() => setStatus("published")} /> Published
                            </label>
                        </div>
                    </div>
                    <div className="col-md-6 form-group">
                        <label>Published date (optional)</label>
                        <input
                            type="date"
                            className="form-control"
                            value={publishedAt}
                            onChange={(e) => setPublishedAt(e.target.value)}
                        />
                        <small className="text-muted">Defaults to today when publishing.</small>
                    </div>
                </div>

                <div className="form-group mt-3">
                    <label>Body *</label>
                    <div className={errors.body ? "border border-danger rounded" : ""}>
                        <BlogEditor
                            content={bodyHtml}
                            onChange={(html, empty) => {
                                setBodyHtml(html);
                                setBodyEmpty(empty);
                            }}
                        />
                    </div>
                    {errors.body && <small className="text-danger">{errors.body}</small>}
                </div>

                <button type="submit" className="btn cta-btn mt-4" disabled={loading}>
                    {buttonName}
                </button>
            </div>
        </form>
    );
};

export default BlogForm;
